import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { UsersIcon, DocumentTextIcon, UserGroupIcon } from '@heroicons/react/24/solid';
import AdminShell from '../../components/layout/AdminShell';
import { adminNavigation } from '../../constants/navigation';
import { api } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  NotificationRule,
  NotificationTriggerType,
  triggerTypeLabels,
  notifyRoleLabels,
  OnboardingStep,
  prospectStatusLabels,
  timelineFieldLabels,
  handoffToRoleLabels,
} from '../../types';

const NotificationSettings: React.FC = () => {
  const { loanOfficer: currentUser, isAdmin } = useAuth();
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'step_completed' as NotificationTriggerType,
    trigger_step_id: '',
    trigger_task_pattern: '',
    trigger_prospect_status: '',
    trigger_timeline_field: '',
    trigger_handoff_to_role: '' as '' | 'admin' | 'va',
    notify_role: 'admin' as 'admin' | 'va' | 'both',
    send_email: true,
    notify_emails: [] as string[],
    cc_emails: [] as string[],
    bcc_emails: [] as string[],
  });

  // Email input states for typing new emails
  const [newNotifyEmail, setNewNotifyEmail] = useState('');
  const [newCcEmail, setNewCcEmail] = useState('');
  const [newBccEmail, setNewBccEmail] = useState('');

  // Fetch rules and steps
  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesData, stepsData] = await Promise.all([
        api.getNotificationRules(),
        api.getOnboardingSteps({ activeOnly: true }),
      ]);
      setRules(rulesData || []);
      setSteps(stepsData || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Open modal for creating new rule
  const handleCreate = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      trigger_type: 'step_completed',
      trigger_step_id: '',
      trigger_task_pattern: '',
      trigger_prospect_status: '',
      trigger_timeline_field: '',
      trigger_handoff_to_role: '',
      notify_role: 'admin',
      send_email: true,
      notify_emails: [],
      cc_emails: [],
      bcc_emails: [],
    });
    setNewNotifyEmail('');
    setNewCcEmail('');
    setNewBccEmail('');
    setError(null);
    setIsModalOpen(true);
  };

  // Open modal for editing existing rule
  const handleEdit = (rule: NotificationRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      trigger_type: rule.trigger_type,
      trigger_step_id: rule.trigger_step_id || '',
      trigger_task_pattern: rule.trigger_task_pattern || '',
      trigger_prospect_status: rule.trigger_prospect_status || '',
      trigger_timeline_field: rule.trigger_timeline_field || '',
      trigger_handoff_to_role: rule.trigger_handoff_to_role || '',
      notify_role: rule.notify_role,
      send_email: rule.send_email,
      notify_emails: rule.notify_emails || [],
      cc_emails: rule.cc_emails || [],
      bcc_emails: rule.bcc_emails || [],
    });
    setNewNotifyEmail('');
    setNewCcEmail('');
    setNewBccEmail('');
    setError(null);
    setIsModalOpen(true);
  };

  // Save rule (create or update)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Rule name is required');
      return;
    }

    // Validate emails if send_email is enabled and no emails provided
    if (formData.send_email && formData.notify_emails.length === 0 && formData.notify_role === 'admin') {
      // Allow saving with just notify_role, no custom emails required
    }

    setSaving(true);
    setError(null);

    try {
      if (editingRule) {
        // Update
        await api.updateNotificationRule(editingRule.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          trigger_type: formData.trigger_type,
          trigger_step_id: formData.trigger_step_id || null,
          trigger_task_pattern: formData.trigger_task_pattern || null,
          trigger_prospect_status: formData.trigger_prospect_status || null,
          trigger_timeline_field: formData.trigger_timeline_field || null,
          trigger_handoff_to_role: formData.trigger_handoff_to_role || null,
          notify_role: formData.notify_role,
          send_email: formData.send_email,
          notify_emails: formData.notify_emails,
          cc_emails: formData.cc_emails,
          bcc_emails: formData.bcc_emails,
        });
      } else {
        // Create
        await api.createNotificationRule({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          trigger_type: formData.trigger_type,
          trigger_step_id: formData.trigger_step_id || undefined,
          trigger_task_pattern: formData.trigger_task_pattern || undefined,
          trigger_prospect_status: formData.trigger_prospect_status || undefined,
          trigger_timeline_field: formData.trigger_timeline_field || undefined,
          trigger_handoff_to_role: formData.trigger_handoff_to_role || undefined,
          notify_role: formData.notify_role,
          send_email: formData.send_email,
          notify_emails: formData.notify_emails,
          cc_emails: formData.cc_emails,
          bcc_emails: formData.bcc_emails,
          created_by: currentUser?.id,
        });
      }

      setIsModalOpen(false);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  // Toggle rule active status
  const handleToggle = async (rule: NotificationRule) => {
    try {
      await api.toggleNotificationRule(rule.id, !rule.is_active);
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, is_active: !r.is_active } : r))
      );
    } catch (err) {
      console.error('Error toggling rule:', err);
    }
  };

  // Delete rule
  const handleDelete = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to delete this notification rule?')) return;

    try {
      await api.deleteNotificationRule(ruleId);
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  // Email helpers
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmail = (field: 'notify_emails' | 'cc_emails' | 'bcc_emails', email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !isValidEmail(trimmed)) return false;
    if (formData[field].includes(trimmed)) return false;
    setFormData(prev => ({ ...prev, [field]: [...prev[field], trimmed] }));
    return true;
  };

  const removeEmail = (field: 'notify_emails' | 'cc_emails' | 'bcc_emails', email: string) => {
    setFormData(prev => ({ ...prev, [field]: prev[field].filter(e => e !== email) }));
  };

  const handleEmailKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: 'notify_emails' | 'cc_emails' | 'bcc_emails',
    value: string,
    setValue: (val: string) => void
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (addEmail(field, value)) {
        setValue('');
      }
    }
  };

  // Get trigger type icon
  const getTriggerIcon = (type: NotificationTriggerType) => {
    switch (type) {
      case 'step_completed':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'task_completed':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'prospect_status_change':
        return <UserGroupIcon className="h-5 w-5" />;
      case 'task_assigned':
        return <UsersIcon className="h-5 w-5" />;
      case 'timeline_stage_change':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'manual_handoff':
        return <UsersIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  if (!isAdmin) {
    return (
      <AdminShell title="Notification Settings" description="Configure automatic alerts and email triggers for team coordination." nav={adminNavigation}>
        <div className="text-center py-12">
          <p className="text-slate-500">Only admins can manage notification settings.</p>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Notification Settings" description="Configure automatic alerts and email triggers for team coordination." nav={adminNavigation}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Notification Rules</h2>
          <p className="text-sm text-slate-500">
            Configure when Admin and VA teams receive notifications
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-lumin-primary text-white text-sm font-medium rounded-lg hover:bg-lumin-primary/90 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Create Rule
        </button>
      </div>

      {/* Rules List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 border-2 border-slate-200 border-t-lumin-primary rounded-full animate-spin" />
        </div>
      ) : rules.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <BellIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No notification rules</h3>
          <p className="text-sm text-slate-500 mb-4">
            Create rules to automatically notify team members when events happen.
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-lumin-primary text-white text-sm font-medium rounded-lg hover:bg-lumin-primary/90 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Create First Rule
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <ul className="divide-y divide-slate-200">
            {rules.map((rule) => (
              <li
                key={rule.id}
                className={`px-5 py-4 hover:bg-slate-50 transition-colors ${
                  !rule.is_active ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 p-2.5 rounded-lg ${
                      rule.is_active ? 'bg-lumin-primary/10 text-lumin-primary' : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {getTriggerIcon(rule.trigger_type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-medium text-slate-900">{rule.name}</h4>
                      {!rule.is_active && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500 rounded">
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {triggerTypeLabels[rule.trigger_type]} &rarr;{' '}
                      {notifyRoleLabels[rule.notify_role as keyof typeof notifyRoleLabels]}
                      {rule.send_email && (
                        <span className="inline-flex items-center gap-1 ml-2 text-slate-400">
                          <EnvelopeIcon className="h-3 w-3" />
                          Email
                          {(rule.notify_emails?.length > 0 || rule.cc_emails?.length > 0 || rule.bcc_emails?.length > 0) && (
                            <span className="text-slate-400">
                              ({rule.notify_emails?.length || 0} to
                              {rule.cc_emails?.length > 0 && `, ${rule.cc_emails.length} cc`}
                              {rule.bcc_emails?.length > 0 && `, ${rule.bcc_emails.length} bcc`})
                            </span>
                          )}
                        </span>
                      )}
                    </p>
                    {rule.description && (
                      <p className="text-xs text-slate-400 mt-0.5">{rule.description}</p>
                    )}
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(rule)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      rule.is_active ? 'bg-lumin-primary' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        rule.is_active ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(rule)}
                      className="p-2 text-slate-400 hover:text-lumin-primary hover:bg-lumin-primary/10 rounded-lg transition-colors"
                      title="Edit rule"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(rule.id)}
                      className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Delete rule"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setIsModalOpen(false)}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              {/* Header */}
              <h2 className="text-lg font-semibold text-slate-900 mb-5">
                {editingRule ? 'Edit Notification Rule' : 'Create Notification Rule'}
              </h2>

              {/* Form */}
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Notify Admin when email created"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                  />
                </div>

                {/* Trigger Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Trigger
                  </label>
                  <select
                    value={formData.trigger_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        trigger_type: e.target.value as NotificationTriggerType,
                        trigger_step_id: '',
                        trigger_task_pattern: '',
                        trigger_prospect_status: '',
                        trigger_timeline_field: '',
                        trigger_handoff_to_role: '',
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                  >
                    {Object.entries(triggerTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Conditional fields based on trigger type */}
                {formData.trigger_type === 'step_completed' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Specific Step (optional)
                    </label>
                    <select
                      value={formData.trigger_step_id}
                      onChange={(e) =>
                        setFormData({ ...formData, trigger_step_id: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                    >
                      <option value="">Any step</option>
                      {steps.map((step) => (
                        <option key={step.id} value={step.id}>
                          {step.task_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.trigger_type === 'task_completed' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Task Title Pattern
                    </label>
                    <input
                      type="text"
                      value={formData.trigger_task_pattern}
                      onChange={(e) =>
                        setFormData({ ...formData, trigger_task_pattern: e.target.value })
                      }
                      placeholder="e.g., Create Company Email"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Matches tasks containing this text
                    </p>
                  </div>
                )}

                {formData.trigger_type === 'prospect_status_change' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Status
                    </label>
                    <select
                      value={formData.trigger_prospect_status}
                      onChange={(e) =>
                        setFormData({ ...formData, trigger_prospect_status: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                    >
                      <option value="">Any status change</option>
                      {Object.entries(prospectStatusLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.trigger_type === 'timeline_stage_change' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Timeline Stage
                    </label>
                    <select
                      value={formData.trigger_timeline_field}
                      onChange={(e) =>
                        setFormData({ ...formData, trigger_timeline_field: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                    >
                      <option value="">Select timeline stage</option>
                      {Object.entries(timelineFieldLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Triggers when this date is set on the timeline
                    </p>
                  </div>
                )}

                {formData.trigger_type === 'manual_handoff' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Handoff Target
                    </label>
                    <select
                      value={formData.trigger_handoff_to_role}
                      onChange={(e) =>
                        setFormData({ ...formData, trigger_handoff_to_role: e.target.value as '' | 'admin' | 'va' })
                      }
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                    >
                      <option value="">Select handoff target</option>
                      {Object.entries(handoffToRoleLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Triggers when a handoff is made to this team
                    </p>
                  </div>
                )}

                {/* Notify Role - for in-app notifications */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    In-App Notification
                  </label>
                  <select
                    value={formData.notify_role}
                    onChange={(e) =>
                      setFormData({ ...formData, notify_role: e.target.value as 'admin' | 'va' | 'both' })
                    }
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                  >
                    <option value="admin">Admin Team</option>
                    <option value="va">VA Team</option>
                    <option value="both">Both Teams</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Shows in the notification bell for selected team(s)
                  </p>
                </div>

                {/* Send Email */}
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.send_email}
                    onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-lumin-primary focus:ring-lumin-primary"
                  />
                  <span className="text-sm text-slate-700">Send email notification</span>
                </label>

                {/* Email Recipients - only show when send_email is enabled */}
                {formData.send_email && (
                  <div className="space-y-4 pt-4 border-t border-slate-200">
                    {/* To Recipients */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        To Recipients
                      </label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.notify_emails.map((email) => (
                          <span
                            key={email}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-lumin-primary/10 text-lumin-primary text-xs rounded-full"
                          >
                            {email}
                            <button
                              type="button"
                              onClick={() => removeEmail('notify_emails', email)}
                              className="hover:text-lumin-primary/70"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="email"
                        value={newNotifyEmail}
                        onChange={(e) => setNewNotifyEmail(e.target.value)}
                        onKeyDown={(e) => handleEmailKeyDown(e, 'notify_emails', newNotifyEmail, setNewNotifyEmail)}
                        onBlur={() => {
                          if (addEmail('notify_emails', newNotifyEmail)) setNewNotifyEmail('');
                        }}
                        placeholder="Enter email and press Enter"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Primary recipients. Leave empty to use role-based recipients only.
                      </p>
                    </div>

                    {/* CC Recipients */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        CC Recipients
                      </label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.cc_emails.map((email) => (
                          <span
                            key={email}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                          >
                            {email}
                            <button
                              type="button"
                              onClick={() => removeEmail('cc_emails', email)}
                              className="hover:text-blue-500"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="email"
                        value={newCcEmail}
                        onChange={(e) => setNewCcEmail(e.target.value)}
                        onKeyDown={(e) => handleEmailKeyDown(e, 'cc_emails', newCcEmail, setNewCcEmail)}
                        onBlur={() => {
                          if (addEmail('cc_emails', newCcEmail)) setNewCcEmail('');
                        }}
                        placeholder="Enter email and press Enter"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                      />
                    </div>

                    {/* BCC Recipients */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        BCC Recipients
                      </label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.bcc_emails.map((email) => (
                          <span
                            key={email}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
                          >
                            {email}
                            <button
                              type="button"
                              onClick={() => removeEmail('bcc_emails', email)}
                              className="hover:text-slate-500"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="email"
                        value={newBccEmail}
                        onChange={(e) => setNewBccEmail(e.target.value)}
                        onKeyDown={(e) => handleEmailKeyDown(e, 'bcc_emails', newBccEmail, setNewBccEmail)}
                        onBlur={() => {
                          if (addEmail('bcc_emails', newBccEmail)) setNewBccEmail('');
                        }}
                        placeholder="Enter email and press Enter"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium bg-lumin-primary text-white rounded-lg hover:bg-lumin-primary/90 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default NotificationSettings;
